<template>
  <div class="cg-container" :class="{'is-printing': isPrinting}" id="pdf-content">
    <!-- Header -->
    <div class="cg-header d-flex justify-space-between align-center px-6 position-relative">
      <!-- Left: Empty space to balance flexbox -->
      <div class="d-flex align-center" style="flex: 1;">
      </div>
      <!-- Center: Logo -->
      <div class="d-flex justify-center align-center" style="flex: 1;">
        <img src="/wtilogo.png" alt="WTI Logo" style="max-height: 45px;" />
      </div>
      <!-- Right: Download Button -->
      <div class="d-flex justify-end align-center" style="flex: 1;">
        <v-btn outlined dark @click="downloadPDF" :loading="isPrinting" class="text-none" :small="$vuetify.breakpoint.smAndDown">
          <v-icon left :small="$vuetify.breakpoint.smAndDown">mdi-download</v-icon> <span class="d-none d-sm-inline">Download</span><span class="d-inline d-sm-none" style="font-size: 12px;">Download</span>
        </v-btn>
      </div>
    </div>

    <div v-if="loading" class="d-flex justify-center align-center" style="height: 100%;">
      <v-progress-circular indeterminate color="#005f32"></v-progress-circular>
    </div>

    <div v-else class="cg-content pa-4">
      
      <!-- Row 1: Info, Quals, Actions -->
      <v-row class="row-1 mb-2" dense>
        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg d-flex flex-column pa-2 cg-card text-left" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold" style="font-size: 14px;">Employee Info</div>
              <v-btn v-if="isAdmin" icon x-small color="#005f32" title="Edit Experience Before WTI" @click="openEditEmpInfoDialog"><v-icon x-small>mdi-pencil</v-icon></v-btn>
            </div>
            <div class="d-flex align-start">
              <v-avatar size="80" rounded class="mr-4 grey lighten-4">
                <img v-if="empData.photo" :src="empData.photo.startsWith('http') ? empData.photo : mediaURL + empData.photo" alt="Photo" style="object-fit: cover; width: 100%; height: 100%;" @error="empData.photo = null">
                <v-icon v-else size="70" color="grey">mdi-account-circle</v-icon>
              </v-avatar>
              <div class="text-left mt-0">
                <div class="text-subtitle-1 font-weight-bold text-uppercase mb-0" style="line-height: 1.1;">{{ empData.name || 'UNKNOWN' }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Emp ID:</strong> {{ empData.employeeId || 'N/A' }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Date Of Joining:</strong> {{ formatDate(empData.dateofjoining) }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Tenure:</strong> {{ calculateTenure(empData.dateofjoining) }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Exp before WTI:</strong> {{ formatExperienceBeforeWTI(empData.experienceBeforeWTI) }}</div>
                <div class="text-caption" style="line-height: 1.2;"><strong>Total Experience:</strong> {{ calculateTotalExperience(empData.dateofjoining, empData.experienceBeforeWTI) }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg pa-2 cg-card d-flex flex-column text-left" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold" style="font-size: 14px;">Qualifications & Skills</div>
              <v-btn v-if="isAdmin" icon x-small color="#005f32" @click="openDialog('qualification')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 overflow-y-auto overflow-x-hidden no-scrollbar" style="min-height: 0;">
                <ul class="text-caption pl-0 mb-0 text-left list-style-none" style="color: #333; line-height: 1.2; word-break: break-word; overflow-x: hidden;">
                <li v-for="(qual, i) in graphData.qualifications"
                    :key="'q'+i"
                    class="mb-1 text-left d-flex justify-space-between align-center"
                    :class="{'dragging-item': draggedSection === 'qualifications' && draggedIndex === i}"
                    :draggable="isAdmin"
                    @dragstart="onDragStart($event, 'qualifications', i)"
                    @dragover.prevent="onDragOver($event)"
                    @drop="onDrop($event, 'qualifications', i)"
                    @dragend="onDragEnd"
                    style="overflow-x: hidden;">
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 mr-1 print-hide cursor-grab" title="Drag to reorder">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="drag-icon-svg mr-1">
                            <path d="M12 2L17 7H7L12 2Z M4 9H20V11H4V9Z M4 12.5H20V14.5H4V12.5Z M4 16H20V18H4V16Z M12 22L7 17H17L12 22Z" />
                        </svg>
                    </div>
                    <span style="word-break: break-word; overflow-wrap: anywhere; min-width: 0;" class="flex-grow-1 mr-2">{{ qual.description }}</span>
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 ml-1 print-hide">
                        <v-icon x-small color="primary" class="mr-1 cursor-pointer" title="Edit" @click="editQualification(qual, i)">mdi-pencil</v-icon>
                        <v-icon x-small color="error" class="cursor-pointer" title="Delete" @click="deleteQualification(qual, i)">mdi-delete</v-icon>
                    </div>
                </li>
                <li v-if="!graphData.qualifications || graphData.qualifications.length === 0" class="grey--text list-style-none">
                    No qualifications added.
                </li>
                </ul>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg pa-2 cg-card d-flex flex-column text-left" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
                <div class="font-weight-bold" style="font-size: 14px;">Disciplinary Action</div>
                <v-btn v-if="isAdmin" icon x-small color="#005f32" @click="openDialog('disciplinary')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 overflow-y-auto overflow-x-hidden no-scrollbar" style="min-height: 0;">
                <ul class="text-caption pl-0 mb-0 text-left list-style-none" style="color: #333; line-height: 1.2; word-break: break-word; overflow-x: hidden;">
                <li v-for="(action, i) in graphData.disciplinaryActions"
                    :key="'da'+i"
                    class="mb-1 text-left d-flex justify-space-between align-center"
                    :class="{'dragging-item': draggedSection === 'disciplinaryActions' && draggedIndex === i}"
                    :draggable="isAdmin"
                    @dragstart="onDragStart($event, 'disciplinaryActions', i)"
                    @dragover.prevent="onDragOver($event)"
                    @drop="onDrop($event, 'disciplinaryActions', i)"
                    @dragend="onDragEnd"
                    style="color: red; overflow-x: hidden;">
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 mr-1 print-hide cursor-grab" title="Drag to reorder">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="drag-icon-svg mr-1">
                            <path d="M12 2L17 7H7L12 2Z M4 9H20V11H4V9Z M4 12.5H20V14.5H4V12.5Z M4 16H20V18H4V16Z M12 22L7 17H17L12 22Z" />
                        </svg>
                    </div>
                    <span style="color: #333; word-break: break-word; overflow-wrap: anywhere; min-width: 0;" class="flex-grow-1 mr-2">{{ action.description }}</span>
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 ml-1 print-hide">
                        <v-icon x-small color="primary" class="mr-1 cursor-pointer" title="Edit" @click="editDisciplinary(action, i)">mdi-pencil</v-icon>
                        <v-icon x-small color="error" class="cursor-pointer" title="Delete" @click="deleteDisciplinary(action, i)">mdi-delete</v-icon>
                    </div>
                </li>
                <li v-if="!graphData.disciplinaryActions || graphData.disciplinaryActions.length === 0" class="grey--text list-style-none">
                    No disciplinary actions on record.
                </li>
                </ul>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 1.5: Achievements, Trainings Undergone, Additional Comments -->
      <v-row class="row-1 mb-2" dense>
        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg pa-2 cg-card d-flex flex-column text-left" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold" style="font-size: 14px;">Achievements</div>
              <v-btn v-if="isAdmin" icon x-small color="#005f32" @click="openDialog('achievement')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 overflow-y-auto overflow-x-hidden no-scrollbar" style="min-height: 0;">
                <ul class="text-caption pl-0 mb-0 text-left list-style-none" style="color: #333; line-height: 1.2; word-break: break-word; overflow-x: hidden;">
                <li v-for="(ach, i) in graphData.achievements"
                    :key="'ach'+i"
                    class="mb-1 text-left d-flex justify-space-between align-center"
                    :class="{'dragging-item': draggedSection === 'achievements' && draggedIndex === i}"
                    :draggable="isAdmin"
                    @dragstart="onDragStart($event, 'achievements', i)"
                    @dragover.prevent="onDragOver($event)"
                    @drop="onDrop($event, 'achievements', i)"
                    @dragend="onDragEnd"
                    style="overflow-x: hidden;">
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 mr-1 print-hide cursor-grab" title="Drag to reorder">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="drag-icon-svg mr-1">
                            <path d="M12 2L17 7H7L12 2Z M4 9H20V11H4V9Z M4 12.5H20V14.5H4V12.5Z M4 16H20V18H4V16Z M12 22L7 17H17L12 22Z" />
                        </svg>
                    </div>
                    <span style="word-break: break-word; overflow-wrap: anywhere; min-width: 0;" class="flex-grow-1 mr-2">{{ ach.description }}</span>
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 ml-1 print-hide">
                        <v-icon x-small color="primary" class="mr-1 cursor-pointer" title="Edit" @click="editAchievement(ach, i)">mdi-pencil</v-icon>
                        <v-icon x-small color="error" class="cursor-pointer" title="Delete" @click="deleteAchievement(ach, i)">mdi-delete</v-icon>
                    </div>
                </li>
                <li v-if="!graphData.achievements || graphData.achievements.length === 0" class="grey--text list-style-none">
                    No achievements added.
                </li>
                </ul>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg pa-2 cg-card d-flex flex-column text-left" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold" style="font-size: 14px;">Trainings Undergone</div>
              <v-btn v-if="isAdmin" icon x-small color="#005f32" @click="openDialog('training')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 overflow-y-auto overflow-x-hidden no-scrollbar" style="min-height: 0;">
                <ul class="text-caption pl-0 mb-0 text-left list-style-none" style="color: #333; line-height: 1.2; word-break: break-word; overflow-x: hidden;">
                <li v-for="(trn, i) in graphData.trainingsUndergone"
                    :key="'trn'+i"
                    class="mb-1 text-left d-flex justify-space-between align-center"
                    :class="{'dragging-item': draggedSection === 'trainingsUndergone' && draggedIndex === i}"
                    :draggable="isAdmin"
                    @dragstart="onDragStart($event, 'trainingsUndergone', i)"
                    @dragover.prevent="onDragOver($event)"
                    @drop="onDrop($event, 'trainingsUndergone', i)"
                    @dragend="onDragEnd"
                    style="overflow-x: hidden;">
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 mr-1 print-hide cursor-grab" title="Drag to reorder">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="drag-icon-svg mr-1">
                            <path d="M12 2L17 7H7L12 2Z M4 9H20V11H4V9Z M4 12.5H20V14.5H4V12.5Z M4 16H20V18H4V16Z M12 22L7 17H17L12 22Z" />
                        </svg>
                    </div>
                    <span style="word-break: break-word; overflow-wrap: anywhere; min-width: 0;" class="flex-grow-1 mr-2">{{ trn.description }}</span>
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 ml-1 print-hide">
                        <v-icon x-small color="primary" class="mr-1 cursor-pointer" title="Edit" @click="editTraining(trn, i)">mdi-pencil</v-icon>
                        <v-icon x-small color="error" class="cursor-pointer" title="Delete" @click="deleteTraining(trn, i)">mdi-delete</v-icon>
                    </div>
                </li>
                <li v-if="!graphData.trainingsUndergone || graphData.trainingsUndergone.length === 0" class="grey--text list-style-none">
                    No trainings recorded.
                </li>
                </ul>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card class="fill-height rounded-lg pa-2 cg-card d-flex flex-column text-left" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
                <div class="font-weight-bold" style="font-size: 14px;">Additional Comments</div>
                <v-btn v-if="isAdmin" icon x-small color="#005f32" @click="openDialog('additionalComment')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            <div class="flex-grow-1 overflow-y-auto overflow-x-hidden no-scrollbar" style="min-height: 0;">
                <ul class="text-caption pl-0 mb-0 text-left list-style-none" style="color: #333; line-height: 1.2; word-break: break-word; overflow-x: hidden;">
                <li v-for="(comm, i) in graphData.additionalComments"
                    :key="'ac'+i"
                    class="mb-1 text-left d-flex justify-space-between align-center"
                    :class="{'dragging-item': draggedSection === 'additionalComments' && draggedIndex === i}"
                    :draggable="isAdmin"
                    @dragstart="onDragStart($event, 'additionalComments', i)"
                    @dragover.prevent="onDragOver($event)"
                    @drop="onDrop($event, 'additionalComments', i)"
                    @dragend="onDragEnd"
                    style="overflow-x: hidden;">
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 mr-1 print-hide cursor-grab" title="Drag to reorder">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" class="drag-icon-svg mr-1">
                            <path d="M12 2L17 7H7L12 2Z M4 9H20V11H4V9Z M4 12.5H20V14.5H4V12.5Z M4 16H20V18H4V16Z M12 22L7 17H17L12 22Z" />
                        </svg>
                    </div>
                    <span style="color: #333; word-break: break-word; overflow-wrap: anywhere; min-width: 0;" class="flex-grow-1 mr-2">{{ comm.description }}</span>
                    <div v-if="isAdmin" class="d-flex align-center flex-shrink-0 ml-1 print-hide">
                        <v-icon x-small color="primary" class="mr-1 cursor-pointer" title="Edit" @click="editAdditionalComment(comm, i)">mdi-pencil</v-icon>
                        <v-icon x-small color="error" class="cursor-pointer" title="Delete" @click="deleteAdditionalComment(comm, i)">mdi-delete</v-icon>
                    </div>
                </li>
                <li v-if="!graphData.additionalComments || graphData.additionalComments.length === 0" class="grey--text list-style-none">
                    No additional comments on record.
                </li>
                </ul>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 2: Timeline -->
      <v-row class="row-2 mb-2" no-gutters>
        <v-col cols="12">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-2 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-0 mt-0">
               <div class="font-weight-bold mx-auto text-subtitle-2">Key Event Timeline</div>
               <v-btn v-if="isAdmin" icon x-small color="#005f32" class="position-absolute" style="right: 16px;" @click="openDialog('timeline')"><v-icon>mdi-plus</v-icon></v-btn>
            </div>
            
            <div class="timeline-wrapper flex-grow-1 position-relative mx-2">
               <div class="position-relative d-flex align-center" style="width: 100%; height: 100%; padding: 0 50px;">
                   <div class="position-relative" style="width: 100%;">
                       <div class="timeline-line" style="left: 0; right: 0; width: 100%;"></div>
                       <div v-for="(event, i) in sortedTimeline"
                            :key="'t'+i"
                            class="timeline-node text-center"
                            :class="{'dragging-item': draggedSection === 'timeline' && draggedIndex === i}"
                            :style="{ left: sortedTimeline.length === 1 ? '50%' : `${(i/(sortedTimeline.length-1))*100}%` }"
                            :draggable="isAdmin"
                            @dragstart="onDragStart($event, 'timeline', i)"
                            @dragover.prevent="onDragOver($event)"
                            @drop="onDrop($event, 'timeline', i)"
                            @dragend="onDragEnd">
                         <div class="timeline-title-top d-flex flex-column align-center justify-center">
                           <template v-if="i % 2 === 0">
                             <div class="d-flex align-center justify-center mb-1">
                               <v-icon v-if="event.title.toLowerCase().includes('join')" x-small color="#005f32" class="mr-1">mdi-account-check</v-icon>
                               <v-icon v-else-if="event.title.toLowerCase().includes('promot')" x-small color="#005f32" class="mr-1">mdi-trending-up</v-icon>
                               <v-icon v-else-if="event.title.toLowerCase().includes('warn')" x-small color="red" class="mr-1">mdi-alert</v-icon>
                               <v-icon v-else x-small color="#005f32" class="mr-1">mdi-star-circle</v-icon>
                               <span class="text-caption text-uppercase text-center" style="font-size: 11px !important; white-space: nowrap; line-height: 1.1;">[{{ event.title }}]</span>
                             </div>
                             <div class="text-caption text-center mt-1" style="font-size: 10px !important; line-height: 1.1;">
                               {{ event.yearCount || event.financialYear }}
                               <span class="grey--text" style="font-size: 10px !important;">({{ formatFY(event.financialYear) || formatDate(event.date) }})</span>
                             </div>
                             <div v-if="isAdmin" class="d-flex align-center justify-center mt-1 print-hide">
                               <div class="d-inline-flex align-center cursor-grab mr-1" title="Drag to reorder">
                                 <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" class="drag-icon-svg" style="transform: rotate(90deg);">
                                     <path d="M12 2L17 7H7L12 2Z M4 9H20V11H4V9Z M4 12.5H20V14.5H4V12.5Z M4 16H20V18H4V16Z M12 22L7 17H17L12 22Z" />
                                 </svg>
                               </div>
                               <v-icon style="font-size: 11px;" color="primary" class="cursor-pointer ml-1" title="Edit Event" @click.stop="editTimeline(event, i)">mdi-pencil</v-icon>
                               <v-icon style="font-size: 11px;" color="error" class="cursor-pointer ml-1" title="Delete Event" @click.stop="deleteTimeline(event, i)">mdi-delete</v-icon>
                             </div>
                           </template>
                         </div>
                         <div class="timeline-dot" :class="{'bg-red': event.title.toLowerCase().includes('warn')}"></div>
                         <div class="timeline-title-bottom">
                           <template v-if="i % 2 !== 0">
                             <div class="d-flex align-center justify-center mb-1">
                               <v-icon v-if="event.title.toLowerCase().includes('join')" x-small color="#005f32" class="mr-1">mdi-account-check</v-icon>
                               <v-icon v-else-if="event.title.toLowerCase().includes('promot')" x-small color="#005f32" class="mr-1">mdi-trending-up</v-icon>
                               <v-icon v-else-if="event.title.toLowerCase().includes('warn')" x-small color="red" class="mr-1">mdi-alert</v-icon>
                               <v-icon v-else x-small color="#005f32" class="mr-1">mdi-star-circle</v-icon>
                               <span class="text-caption text-uppercase text-center" style="font-size: 11px !important; white-space: nowrap; line-height: 1.1;">[{{ event.title }}]</span>
                             </div>
                             <div class="text-caption text-center mt-1" style="font-size: 10px !important; line-height: 1.1;">
                               {{ event.yearCount || event.financialYear }}
                               <span class="grey--text" style="font-size: 10px !important;">({{ formatFY(event.financialYear) || formatDate(event.date) }})</span>
                             </div>
                             <div v-if="isAdmin" class="d-flex align-center justify-center mt-1 print-hide">
                               <div class="d-inline-flex align-center cursor-grab mr-1" title="Drag to reorder">
                                 <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" class="drag-icon-svg" style="transform: rotate(90deg);">
                                     <path d="M12 2L17 7H7L12 2Z M4 9H20V11H4V9Z M4 12.5H20V14.5H4V12.5Z M4 16H20V18H4V16Z M12 22L7 17H17L12 22Z" />
                                 </svg>
                               </div>
                               <v-icon style="font-size: 11px;" color="primary" class="cursor-pointer ml-1" title="Edit Event" @click.stop="editTimeline(event, i)">mdi-pencil</v-icon>
                               <v-icon style="font-size: 11px;" color="error" class="cursor-pointer ml-1" title="Delete Event" @click.stop="deleteTimeline(event, i)">mdi-delete</v-icon>
                             </div>
                           </template>
                         </div>
                       </div>
                   </div>
               </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Row 3 Options -->
      <div class="d-flex justify-end align-center mb-1 print-hide custom-small-toggles">
         <v-checkbox v-model="showCTCTrend" label="Show CTC Trend" hide-details class="mt-0 pt-0 mr-4" dense :ripple="false"></v-checkbox>
         <v-switch v-model="singleViewMode" label="Single View Mode" hide-details class="mt-0 pt-0" dense :ripple="false"></v-switch>
      </div>

      <!-- Row 3: Charts -->
      <v-row class="row-3 mb-2" dense v-if="!singleViewMode">
        <!-- CTC -->
        <v-col cols="12" :md="showCTCTrend ? 4 : 0" v-if="showCTCTrend">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold mx-auto text-subtitle-2">CTC Trend (K)</div>
              <div v-if="isAdmin" class="position-absolute d-flex align-center" style="right: 16px;">
                <v-btn icon small color="#005f32" class="mr-1" title="Manage CTC Entries" @click="openManageGraphData('ctc')"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="#005f32" title="Add CTC Entry" @click="openDialog('graphData', 'ctc')"><v-icon>mdi-plus</v-icon></v-btn>
              </div>
            </div>
            <div class="flex-grow-1 chart-container mt-1">
              <apexchart :key="'ctc-' + showCTCTrend + '-' + singleViewMode" type="area" height="100%" width="100%" :options="ctcChartOptions" :series="ctcSeries"></apexchart>
            </div>
          </v-card>
        </v-col>
        <!-- Grade -->
        <v-col cols="12" :md="showCTCTrend ? 4 : 6">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold mx-auto text-subtitle-2">Grade Progression</div>
              <div v-if="isAdmin" class="position-absolute d-flex align-center" style="right: 16px;">
                <v-btn icon small color="#005f32" class="mr-1" title="Manage Grade Entries" @click="openManageGraphData('grade')"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="#005f32" title="Add Grade Entry" @click="openDialog('graphData', 'grade')"><v-icon>mdi-plus</v-icon></v-btn>
              </div>
            </div>
            <div class="flex-grow-1 chart-container mt-1">
              <apexchart :key="'grade-' + showCTCTrend + '-' + singleViewMode" type="line" height="100%" width="100%" :options="gradeChartOptions" :series="gradeSeries"></apexchart>
            </div>
          </v-card>
        </v-col>
        <!-- PMR -->
        <v-col cols="12" :md="showCTCTrend ? 4 : 6">
          <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden" outlined>
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-bold mx-auto text-subtitle-2">PMR Rating</div>
              <div v-if="isAdmin" class="position-absolute d-flex align-center" style="right: 16px;">
                <v-btn icon small color="#005f32" class="mr-1" title="Manage PMR Entries" @click="openManageGraphData('pmr')"><v-icon small>mdi-pencil</v-icon></v-btn>
                <v-btn icon small color="#005f32" title="Add PMR Entry" @click="openDialog('graphData', 'pmr')"><v-icon>mdi-plus</v-icon></v-btn>
              </div>
            </div>
            <div class="flex-grow-1 chart-container mt-1">
              <apexchart :key="'pmr-' + showCTCTrend + '-' + singleViewMode" type="bar" height="100%" width="100%" :options="pmrChartOptions" :series="pmrSeries"></apexchart>
            </div>
          </v-card>
        </v-col>
      </v-row>
      
      <v-row class="row-3 mb-2" dense v-else>
         <v-col cols="12">
            <v-card class="fill-height rounded-lg pt-1 px-2 pb-1 cg-card d-flex flex-column overflow-hidden position-relative" outlined>
                <div class="d-flex justify-space-between align-center mb-1">
                    <div class="font-weight-bold mx-auto text-subtitle-2">Combined Chart</div>
                </div>

                <div class="flex-grow-1 chart-container mt-1">
                    <apexchart :key="'combined-' + showCTCTrend + '-' + singleViewMode" type="line" height="100%" width="100%" :options="combinedChartOptions" :series="combinedSeries"></apexchart>
                </div>
            </v-card>
         </v-col>
      </v-row>
      <!-- Universal Grade Legend (Visible only in single view, keeps space to prevent layout jump) -->
      <div class="d-flex justify-start align-center mt-1" :style="{ color: '#555', fontSize: '11px', visibility: singleViewMode ? 'visible' : 'hidden' }">
          <strong>Grade Legend:</strong>&nbsp; I=7 &nbsp;|&nbsp; H=6.5 &nbsp;|&nbsp; G=6 &nbsp;|&nbsp; F=5.5 &nbsp;|&nbsp; E4=5 &nbsp;|&nbsp; E3=4.5 &nbsp;|&nbsp; E2=4 &nbsp;|&nbsp; E1=3.5 &nbsp;|&nbsp; D=3 &nbsp;|&nbsp; C2=2.5 &nbsp;|&nbsp; C1=2 &nbsp;|&nbsp; B=1.5 &nbsp;|&nbsp; A=1
      </div>

    </div>

    <!-- Dialogs -->
    <v-dialog v-model="dialogs.editEmpInfo" max-width="450px">
        <v-card>
            <v-card-title>Edit Employee Info</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field :value="empData.name" label="Name" outlined dense disabled hide-details class="mb-3"></v-text-field>
                <v-text-field :value="empData.employeeId" label="Emp ID" outlined dense disabled hide-details class="mb-3"></v-text-field>
                <v-text-field v-model="forms.editEmpInfo.dateofjoining" label="Date of Joining" type="date" outlined dense hide-details class="mb-3"></v-text-field>
                <v-text-field v-model="forms.editEmpInfo.experienceBeforeWTI" label="Exp Before WTI (e.g. 4 Years)" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editEmpInfo = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateEmpInfo" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.qualification" max-width="500px">
        <v-card>
            <v-card-title>Add Qualification</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.qualification.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.qualification = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitQualification" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.editQualification" max-width="500px">
        <v-card>
            <v-card-title>Edit Qualification</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.editQualification.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editQualification = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateQualification" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.disciplinary" max-width="500px">
        <v-card>
            <v-card-title>Add Disciplinary Action or Additional Comments</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.disciplinary.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.disciplinary = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitDisciplinary" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.editDisciplinary" max-width="500px">
        <v-card>
            <v-card-title>Edit Disciplinary Action</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.editDisciplinary.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editDisciplinary = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateDisciplinary" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.achievement" max-width="500px">
        <v-card>
            <v-card-title>Add Achievement</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.achievement.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.achievement = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitAchievement" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.editAchievement" max-width="500px">
        <v-card>
            <v-card-title>Edit Achievement</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.editAchievement.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editAchievement = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateAchievement" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.training" max-width="500px">
        <v-card>
            <v-card-title>Add Training Undergone</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.training.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.training = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitTraining" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.editTraining" max-width="500px">
        <v-card>
            <v-card-title>Edit Training Undergone</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.editTraining.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editTraining = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateTraining" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.additionalComment" max-width="500px">
        <v-card>
            <v-card-title>Add Additional Comment</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.additionalComment.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.additionalComment = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitAdditionalComment" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.editAdditionalComment" max-width="500px">
        <v-card>
            <v-card-title>Edit Additional Comment</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.editAdditionalComment.description" label="Description" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editAdditionalComment = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateAdditionalComment" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.timeline" max-width="500px">
        <v-card>
            <v-card-title>Add Timeline Event</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.timeline.title" label="Title (e.g. Promoted to AM)" outlined dense hide-details class="mb-3"></v-text-field>
                <v-text-field v-model="forms.timeline.financialYear" label="Financial Year (e.g. 2023-2024)" outlined dense hide-details class="mb-3"></v-text-field>
                <v-text-field v-model="forms.timeline.yearCount" label="Year Count (e.g. Year 5)" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.timeline = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitTimeline" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.editTimeline" max-width="500px">
        <v-card>
            <v-card-title>Edit Timeline Event</v-card-title>
            <v-card-text class="pt-2">
                <v-text-field v-model="forms.editTimeline.title" label="Title (e.g. Promoted to AM)" outlined dense hide-details class="mb-3"></v-text-field>
                <v-text-field v-model="forms.editTimeline.financialYear" label="Financial Year (e.g. 2023-2024)" outlined dense hide-details class="mb-3"></v-text-field>
                <v-text-field v-model="forms.editTimeline.yearCount" label="Year Count (e.g. Year 5)" outlined dense hide-details class="mb-1"></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editTimeline = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateTimeline" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.graphData" max-width="400px">
        <v-card>
            <v-card-title>
                Add {{ graphDataType === 'ctc' ? 'CTC Data' : (graphDataType === 'grade' ? 'Grade Progression' : 'PMR Rating') }}
            </v-card-title>
            <v-card-text>
                <v-text-field v-model="forms.graphData.financialYear" label="Financial Year (e.g. 2023-2024)" outlined dense class="mb-3"></v-text-field>
                <v-text-field v-model="forms.graphData.yearCount" label="Year Count (e.g. Year 5)" outlined dense class="mb-3"></v-text-field>
                <v-text-field v-if="graphDataType === 'ctc'" v-model="forms.graphData.ctc" label="CTC Amount" type="number" outlined dense hide-details></v-text-field>
                <v-text-field v-if="graphDataType === 'grade'" v-model="forms.graphData.grade" label="Grade (e.g. F or 4.5)" outlined dense hide-details></v-text-field>
                <v-text-field v-if="graphDataType === 'pmr'" v-model="forms.graphData.pmr" label="PMR Rating" type="number" outlined dense hide-details></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.graphData = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="submitGraphData" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.manageGraphData" max-width="600px">
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span>Manage {{ manageType === 'ctc' ? 'CTC Trend' : (manageType === 'grade' ? 'Grade Progression' : 'PMR Rating') }} Entries</span>
                <v-btn icon @click="dialogs.manageGraphData = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-simple-table dense>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">Year Count</th>
                                <th class="text-left">Financial Year</th>
                                <th class="text-left">Value</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, i) in (manageType === 'ctc' ? graphData.ctcTrend : (manageType === 'grade' ? graphData.gradeProgression : graphData.pmrTrend)) || []" :key="i">
                                <td class="text-left">{{ item.yearCount }}</td>
                                <td class="text-left">{{ item.financialYear }}</td>
                                <td class="text-left">{{ manageType === 'ctc' ? item.amount : (manageType === 'grade' ? item.grade : item.rating) }}</td>
                                <td class="text-right">
                                    <v-icon small color="primary" class="mr-2 cursor-pointer" title="Edit Row" @click="editGraphDataRow(item, i)">mdi-pencil</v-icon>
                                    <v-icon small color="error" class="cursor-pointer" title="Delete Row" @click="deleteGraphDataRow(item, i)">mdi-delete</v-icon>
                                </td>
                            </tr>
                            <tr v-if="!((manageType === 'ctc' ? graphData.ctcTrend : (manageType === 'grade' ? graphData.gradeProgression : graphData.pmrTrend)) || []).length">
                                <td colspan="4" class="text-center grey--text">No entries found.</td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.manageGraphData = false">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.editGraphDataRow" max-width="400px">
        <v-card>
            <v-card-title>
                Edit {{ forms.editGraphDataRow.type === 'ctc' ? 'CTC Data' : (forms.editGraphDataRow.type === 'grade' ? 'Grade Progression' : 'PMR Rating') }}
            </v-card-title>
            <v-card-text>
                <v-text-field v-model="forms.editGraphDataRow.financialYear" label="Financial Year (e.g. 2023-2024)" outlined dense class="mb-3"></v-text-field>
                <v-text-field v-model="forms.editGraphDataRow.yearCount" label="Year Count (e.g. Year 5)" outlined dense class="mb-3"></v-text-field>
                <v-text-field v-if="forms.editGraphDataRow.type === 'ctc'" v-model="forms.editGraphDataRow.ctc" label="CTC Amount" type="number" outlined dense hide-details></v-text-field>
                <v-text-field v-if="forms.editGraphDataRow.type === 'grade'" v-model="forms.editGraphDataRow.grade" label="Grade (e.g. F or 4.5)" outlined dense hide-details></v-text-field>
                <v-text-field v-if="forms.editGraphDataRow.type === 'pmr'" v-model="forms.editGraphDataRow.pmr" label="PMR Rating" type="number" outlined dense hide-details></v-text-field>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text @click="dialogs.editGraphDataRow = false">Cancel</v-btn>
                <v-btn color="#005f32" class="white--text" @click="updateGraphDataRow" :loading="saving">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import VueApexCharts from 'vue-apexcharts';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

export default {
  name: 'CareerGraphView',
  components: {
    apexchart: VueApexCharts
  },
  data() {
    return { 
      draggedSection: null,
      draggedIndex: null,
      graphData: {},
      loading: true,
      saving: false,
      isPrinting: false,
      showCTCTrend: true,
      singleViewMode: false,
      baseDocUrl: this.baseURL ? this.baseURL + 'u/' : 'http://localhost:5000/u/',
      graphDataType: 'ctc',
      manageType: 'ctc',
      dialogs: {
          qualification: false,
          disciplinary: false,
          achievement: false,
          training: false,
          additionalComment: false,
          timeline: false,
          graphData: false,
          editEmpInfo: false,
          editQualification: false,
          editDisciplinary: false,
          editAchievement: false,
          editTraining: false,
          editAdditionalComment: false,
          editTimeline: false,
          manageGraphData: false,
          editGraphDataRow: false
      },
      forms: {
          qualification: { description: '' },
          disciplinary: { description: '' },
          achievement: { description: '' },
          training: { description: '' },
          additionalComment: { description: '' },
          timeline: { title: '', financialYear: '', yearCount: '' },
          graphData: { financialYear: '', yearCount: '', ctc: null, grade: '', pmr: null },
          editEmpInfo: { dateofjoining: '', experienceBeforeWTI: '' },
          editQualification: { _id: null, index: null, description: '' },
          editDisciplinary: { _id: null, index: null, description: '' },
          editAchievement: { _id: null, index: null, description: '' },
          editTraining: { _id: null, index: null, description: '' },
          editAdditionalComment: { _id: null, index: null, description: '' },
          editTimeline: { _id: null, index: null, title: '', financialYear: '', yearCount: '' },
          editGraphDataRow: { _id: null, index: null, type: '', financialYear: '', yearCount: '', ctc: null, grade: '', pmr: null }
      }
    };
  },
  computed: {
    isAdmin() {
        return this.$store.getters.isAdmin;
    },
    empData() {
        return this.graphData.employeeId || {};
    },
    sortedTimeline() {
        if(!this.graphData.timeline) return [];
        return this.graphData.timeline;
    },
    chartCategories() {
        let cats = new Set();
        (this.graphData.ctcTrend || []).forEach(d => cats.add(d.yearCount));
        (this.graphData.gradeProgression || []).forEach(d => cats.add(d.yearCount));
        (this.graphData.pmrTrend || []).forEach(d => cats.add(d.yearCount));
        let arr = Array.from(cats);
        arr.sort((a, b) => {
            let numA = parseInt(a.replace(/[^\d]/g, '')) || 0;
            let numB = parseInt(b.replace(/[^\d]/g, '')) || 0;
            return numA - numB;
        });
        return arr;
    },
    
    // CTC
    ctcCategories() {
        let arr = (this.graphData.ctcTrend || []).map(d => d.yearCount);
        return [...new Set(arr)].sort((a,b) => (parseInt(a.replace(/[^\d]/g, ''))||0) - (parseInt(b.replace(/[^\d]/g, ''))||0));
    },
    ctcSeries() {
        const data = this.ctcCategories.map(cat => this.getValForCategory('ctc', cat) || null);
        return [{ name: 'CTC', data: data }];
    },
    ctcChartOptions() {
        const ctcData = this.ctcSeries[0].data.filter(d => d !== null);
        let minCtc = undefined;
        if (ctcData.length > 0) {
            // Start from initial salary rounded down to nearest 10k (e.g., 32k -> 30k)
            let initial = ctcData[0];
            minCtc = Math.floor(initial / 10000) * 10000;
        }
        return {
            chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            colors: ['#005f32'],
            dataLabels: { 
                enabled: this.isPrinting,
                formatter: (val) => val ? (val/1000).toFixed(1) + 'k' : '',
                style: { fontSize: '10px' }
            },
            stroke: { curve: 'straight', width: 2 },
            fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.0, stops: [0, 100] } },
            xaxis: { 
                categories: this.ctcCategories.map(cat => [
                    cat.replace('Year ', ''),
                    this.formatFY(this.getFYForCategory(cat))
                ]),
                axisBorder: { show: false }, 
                axisTicks: { show: false },
                labels: { style: { fontSize: '10px' } },
                tooltip: { enabled: false }
            },
            yaxis: { 
                min: minCtc,
                labels: { formatter: (val) => val ? (val/1000).toFixed(1) + 'k' : '', style: { fontSize: '10px' } },
                axisBorder: { show: false }
            },
            grid: { show: false, padding: { top: -10, bottom: 10, left: 10, right: 10 } }
        };
    },

    // Grade
    gradeCategories() {
        let arr = (this.graphData.gradeProgression || []).map(d => d.yearCount);
        return [...new Set(arr)].sort((a,b) => (parseInt(a.replace(/[^\d]/g, ''))||0) - (parseInt(b.replace(/[^\d]/g, ''))||0));
    },
    gradeSeries() {
        const gradeMap = { 'A': 1, 'B': 1.5, 'C1': 2, 'C2': 2.5, 'D': 3, 'E1': 3.5, 'E2': 4, 'E3': 4.5, 'E4': 5, 'F': 5.5, 'G': 6, 'H': 6.5, 'I': 7 };
        const data = this.gradeCategories.map(cat => {
            let g = this.getValForCategory('grade', cat);
            if (!g) return null;
            g = String(g).trim().toUpperCase();
            if (gradeMap[g]) return gradeMap[g];
            let parsed = parseFloat(g);
            return isNaN(parsed) ? null : parsed;
        });
        return [{ name: 'Grade', data: data }];
    },
    gradeChartOptions() {
        const gradeData = this.gradeSeries[0].data.filter(d => d !== null);
        let minGrade = undefined;
        let maxGrade = undefined;
        let tickAmt = undefined;
        if (gradeData.length > 0) {
            minGrade = gradeData[0]; // exact initial grade value
            maxGrade = 7; // Always set highest grade to 'I' (numeric value 7)
            if (minGrade === maxGrade) {
                minGrade -= 0.5; // Ensure chart renders if min == max
            }
            tickAmt = Math.round((maxGrade - minGrade) / 0.5);
        }

        return {
            chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            colors: ['#005f32'],
            dataLabels: { 
                enabled: this.isPrinting,
                formatter: (val) => {
                    const rMap = { 1: 'A', 1.5: 'B', 2: 'C1', 2.5: 'C2', 3: 'D', 3.5: 'E1', 4: 'E2', 4.5: 'E3', 5: 'E4', 5.5: 'F', 6: 'G', 6.5: 'H', 7: 'I' };
                    let rounded = Math.round(val * 10) / 10;
                    return rMap[rounded] || rounded.toFixed(1);
                },
                style: { fontSize: '10px' },
                background: { enabled: true, foreColor: '#ffffff', color: '#005f32', borderRadius: 2, padding: 4 }
            },
            stroke: { curve: 'straight', width: 2 },
            markers: { size: 0, hover: { size: 4 }, colors: ['#005f32'], strokeColors: '#fff', strokeWidth: 1 },
            xaxis: { 
                categories: this.gradeCategories.map(cat => [
                    cat.replace('Year ', ''),
                    this.formatFY(this.getFYForCategory(cat))
                ]),
                axisBorder: { show: false }, 
                axisTicks: { show: false },
                labels: { style: { fontSize: '10px' } },
                tooltip: { enabled: false }
            },
            yaxis: { 
                min: minGrade,
                max: maxGrade,
                tickAmount: tickAmt,
                labels: { 
                    formatter: (val) => {
                        const rMap = { 1: 'A', 1.5: 'B', 2: 'C1', 2.5: 'C2', 3: 'D', 3.5: 'E1', 4: 'E2', 4.5: 'E3', 5: 'E4', 5.5: 'F', 6: 'G', 6.5: 'H', 7: 'I' };
                        let rounded = Math.round(val * 10) / 10;
                        return rMap[rounded] || rounded.toFixed(1);
                    }, 
                    style: { fontSize: '10px' } 
                },
                axisBorder: { show: false }
            },
            grid: { show: false, padding: { top: -10, bottom: 10, left: 10, right: 10 } }
        };
    },

    // PMR
    pmrCategories() {
        let arr = (this.graphData.pmrTrend || []).map(d => d.yearCount);
        return [...new Set(arr)].sort((a,b) => (parseInt(a.replace(/[^\d]/g, ''))||0) - (parseInt(b.replace(/[^\d]/g, ''))||0));
    },
    pmrSeries() {
        const data = this.pmrCategories.map(cat => this.getValForCategory('pmr', cat) || null);
        return [{ name: 'PMR Rating', data: data }];
    },
    pmrChartOptions() {
        return {
            chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            colors: ['#005f32'],
            dataLabels: { 
                enabled: this.isPrinting,
                formatter: (val) => val ? val.toFixed(1) : '',
                style: { fontSize: '10px' }
            },
            plotOptions: { bar: { borderRadius: 0, columnWidth: '40%' } },
            xaxis: { 
                categories: this.pmrCategories.map(cat => [
                    cat.replace('Year ', ''),
                    this.formatFY(this.getFYForCategory(cat))
                ]),
                axisBorder: { show: false }, 
                axisTicks: { show: false },
                labels: { style: { fontSize: '10px' } },
                tooltip: { enabled: false }
            },
            yaxis: { 
                min: 2.0,
                max: 10.0,
                tickAmount: 8,
                labels: { formatter: (val) => val ? val.toFixed(1) : '', style: { fontSize: '10px' } },
                axisBorder: { show: false }
            },
            grid: { show: false, padding: { top: -10, bottom: 10, left: 10, right: 10 } }
        };
    },

    combinedSeries() {
        let series = [];
        const ctcData = this.chartCategories.map(cat => this.getValForCategory('ctc', cat) || null);
        const pmrData = this.chartCategories.map(cat => this.getValForCategory('pmr', cat) || null);
        const gradeMap = { 'A': 1, 'B': 1.5, 'C1': 2, 'C2': 2.5, 'D': 3, 'E1': 3.5, 'E2': 4, 'E3': 4.5, 'E4': 5, 'F': 5.5, 'G': 6, 'H': 6.5, 'I': 7 };
        const gradeData = this.chartCategories.map(cat => {
            let g = this.getValForCategory('grade', cat);
            if (!g) return null;
            g = String(g).trim().toUpperCase();
            if (gradeMap[g]) return gradeMap[g];
            let parsed = parseFloat(g);
            return isNaN(parsed) ? null : parsed;
        });

        if (this.showCTCTrend) {
            series.push({ name: 'CTC', type: 'line', data: ctcData });
        }
        series.push({ name: 'PMR Rating', type: 'column', data: pmrData });
        series.push({ name: 'Grade (Numeric)', type: 'line', data: gradeData });
        return series;
    },
    combinedChartOptions() {
        let yaxisConfig = [];
        let colorsConfig = [];
        let strokeWidths = [];

        if (this.showCTCTrend) {
            colorsConfig.push('#008FFB');
            strokeWidths.push(3);
            yaxisConfig.push({ 
                seriesName: 'CTC', 
                title: { text: 'CTC', style: { fontWeight: 'bold' } },
                labels: { formatter: (val) => val ? val.toFixed(1) : '' },
                axisBorder: { show: false }
            });
        }

        colorsConfig.push('#00E396', '#FEB019');
        strokeWidths.push(0, 3);
        
        yaxisConfig.push({ 
            seriesName: 'PMR Rating', 
            opposite: this.showCTCTrend, 
            title: { text: 'PMR / Grade', style: { fontWeight: 'bold' } }, 
            min: 0, 
            max: 10,
            labels: { formatter: (val) => val ? val.toFixed(1) : '' },
            axisBorder: { show: false }
        });
        
        yaxisConfig.push({ 
            seriesName: 'Grade (Numeric)', 
            opposite: this.showCTCTrend, 
            show: false, 
            min: 0, 
            max: 10 
        });

        return {
            chart: { stacked: false, toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: !this.isPrinting } },
            stroke: { width: strokeWidths, curve: 'smooth' },
            colors: colorsConfig,
            dataLabels: { 
                enabled: false
            },
            xaxis: { 
                categories: this.chartCategories.map(cat => [cat.replace('Year ', ''), this.formatFY(this.getFYForCategory(cat))]),
                axisBorder: { show: false },
                axisTicks: { show: false }
            },
            yaxis: yaxisConfig,
            legend: { position: 'bottom', markers: { radius: 12 } },
            grid: { show: true, borderColor: '#e0e0e0', strokeDashArray: 0, padding: { top: -10, bottom: 10, left: 10, right: 10 } }
        };
    }
  },
  async created() {
    await this.fetchGraphData();
  },
  methods: {
    async fetchGraphData() {
      try {
        const employeeId = this.$route.params.employeeId || this.$route.query.employeeId;
        const token = localStorage.getItem('token');
        const res = await axios.get('/career-graph/' + employeeId, { 
          headers: { 'x-auth-token': token } 
        });
        if (res.data.status) {
          this.graphData = res.data.data;
        }
      } catch (e) {
        console.error("Error fetching graph data:", e);
      } finally {
        this.loading = false;
      }
    },
    openDialog(type, subType) {
        if (type === 'graphData') {
            this.graphDataType = subType;
            
            this.forms.graphData.yearCount = '';
            this.forms.graphData.financialYear = '';
            this.forms.graphData.ctc = null;
            this.forms.graphData.grade = '';
            this.forms.graphData.pmr = null;
        }
        this.dialogs[type] = true;
    },
    async submitQualification() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const payload = {
                employeeId,
                qualification: {
                    title: 'Educational Qualification',
                    description: this.forms.qualification.description,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/qualification', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.qualification = false;
            this.forms.qualification.description = '';
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitDisciplinary() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const payload = {
                employeeId,
                action: {
                    description: this.forms.disciplinary.description,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/disciplinary-action', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.disciplinary = false;
            this.forms.disciplinary = { description: '' };
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitAchievement() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const payload = {
                employeeId,
                achievement: {
                    title: 'Achievement',
                    description: this.forms.achievement.description,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/achievement', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.achievement = false;
            this.forms.achievement.description = '';
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitTraining() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const payload = {
                employeeId,
                training: {
                    title: 'Training Undergone',
                    description: this.forms.training.description,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/training', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.training = false;
            this.forms.training.description = '';
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitAdditionalComment() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const payload = {
                employeeId,
                comment: {
                    title: 'Additional Comment',
                    description: this.forms.additionalComment.description,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/additional-comment', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.additionalComment = false;
            this.forms.additionalComment.description = '';
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitTimeline() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            
            let formattedYearCount = (this.forms.timeline.yearCount || '').trim();
            if (/^\d+$/.test(formattedYearCount)) {
                formattedYearCount = 'Year ' + formattedYearCount;
            }

            const payload = {
                employeeId,
                event: {
                    title: this.forms.timeline.title,
                    financialYear: this.forms.timeline.financialYear,
                    yearCount: formattedYearCount,
                    date: new Date()
                }
            };
            await axios.post('/career-graph/event', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.timeline = false;
            this.forms.timeline = { title: '', financialYear: '', yearCount: '' };
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async submitGraphData() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            
            let formattedYearCount = (this.forms.graphData.yearCount || '').trim();
            if (/^\d+$/.test(formattedYearCount)) {
                formattedYearCount = 'Year ' + formattedYearCount;
            }

            const payload = {
                employeeId,
                financialYear: this.forms.graphData.financialYear,
                yearCount: formattedYearCount,
                ctc: this.forms.graphData.ctc,
                grade: this.forms.graphData.grade,
                pmr: this.forms.graphData.pmr
            };
            await axios.post('/career-graph/graph-data', payload, { headers: { 'x-auth-token': token } });
            this.dialogs.graphData = false;
            this.forms.graphData = { financialYear: '', yearCount: '', ctc: null, grade: '', pmr: null };
            await this.fetchGraphData();
        } catch(e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    openEditEmpInfoDialog() {
        let dojFormatted = '';
        if (this.empData.dateofjoining) {
            const d = new Date(this.empData.dateofjoining);
            if (!isNaN(d.getTime())) {
                dojFormatted = d.toISOString().split('T')[0];
            }
        }
        this.forms.editEmpInfo = {
            dateofjoining: dojFormatted,
            experienceBeforeWTI: this.empData.experienceBeforeWTI || ''
        };
        this.dialogs.editEmpInfo = true;
    },
    async updateEmpInfo() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            await axios.put('/career-graph/emp-info/' + employeeId, {
                dateofjoining: this.forms.editEmpInfo.dateofjoining,
                experienceBeforeWTI: this.forms.editEmpInfo.experienceBeforeWTI
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editEmpInfo = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },

    editQualification(item, index) {
        this.forms.editQualification = {
            _id: item._id || null,
            index: index,
            description: item.description || ''
        };
        this.dialogs.editQualification = true;
    },
    async updateQualification() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = this.forms.editQualification._id || 'none';
            await axios.put('/career-graph/qualification/' + itemId, {
                employeeId,
                index: this.forms.editQualification.index,
                description: this.forms.editQualification.description
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editQualification = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async deleteQualification(item, index) {
        if (!confirm('Are you sure you want to delete this qualification?')) return;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = item._id || 'none';
            await axios.delete('/career-graph/qualification/' + employeeId + '/' + itemId + '?index=' + index, {
                headers: { 'x-auth-token': token }
            });
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        }
    },

    editDisciplinary(item, index) {
        this.forms.editDisciplinary = {
            _id: item._id || null,
            index: index,
            description: item.description || ''
        };
        this.dialogs.editDisciplinary = true;
    },
    async updateDisciplinary() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = this.forms.editDisciplinary._id || 'none';
            await axios.put('/career-graph/disciplinary-action/' + itemId, {
                employeeId,
                index: this.forms.editDisciplinary.index,
                description: this.forms.editDisciplinary.description
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editDisciplinary = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async deleteDisciplinary(item, index) {
        if (!confirm('Are you sure you want to delete this disciplinary action?')) return;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = item._id || 'none';
            await axios.delete('/career-graph/disciplinary-action/' + employeeId + '/' + itemId + '?index=' + index, {
                headers: { 'x-auth-token': token }
            });
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        }
    },

    editAchievement(item, index) {
        this.forms.editAchievement = {
            _id: item._id || null,
            index: index,
            description: item.description || ''
        };
        this.dialogs.editAchievement = true;
    },
    async updateAchievement() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = this.forms.editAchievement._id || 'none';
            await axios.put('/career-graph/achievement/' + itemId, {
                employeeId,
                index: this.forms.editAchievement.index,
                description: this.forms.editAchievement.description
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editAchievement = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async deleteAchievement(item, index) {
        if (!confirm('Are you sure you want to delete this achievement?')) return;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = item._id || 'none';
            await axios.delete('/career-graph/achievement/' + employeeId + '/' + itemId + '?index=' + index, {
                headers: { 'x-auth-token': token }
            });
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        }
    },

    editTraining(item, index) {
        this.forms.editTraining = {
            _id: item._id || null,
            index: index,
            description: item.description || ''
        };
        this.dialogs.editTraining = true;
    },
    async updateTraining() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = this.forms.editTraining._id || 'none';
            await axios.put('/career-graph/training/' + itemId, {
                employeeId,
                index: this.forms.editTraining.index,
                description: this.forms.editTraining.description
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editTraining = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async deleteTraining(item, index) {
        if (!confirm('Are you sure you want to delete this training?')) return;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = item._id || 'none';
            await axios.delete('/career-graph/training/' + employeeId + '/' + itemId + '?index=' + index, {
                headers: { 'x-auth-token': token }
            });
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        }
    },

    editAdditionalComment(item, index) {
        this.forms.editAdditionalComment = {
            _id: item._id || null,
            index: index,
            description: item.description || ''
        };
        this.dialogs.editAdditionalComment = true;
    },
    async updateAdditionalComment() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = this.forms.editAdditionalComment._id || 'none';
            await axios.put('/career-graph/additional-comment/' + itemId, {
                employeeId,
                index: this.forms.editAdditionalComment.index,
                description: this.forms.editAdditionalComment.description
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editAdditionalComment = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async deleteAdditionalComment(item, index) {
        if (!confirm('Are you sure you want to delete this additional comment?')) return;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = item._id || 'none';
            await axios.delete('/career-graph/additional-comment/' + employeeId + '/' + itemId + '?index=' + index, {
                headers: { 'x-auth-token': token }
            });
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        }
    },

    editTimeline(item, index) {
        this.forms.editTimeline = {
            _id: item._id || null,
            index: index,
            title: item.title || '',
            financialYear: item.financialYear || '',
            yearCount: item.yearCount || ''
        };
        this.dialogs.editTimeline = true;
    },
    async updateTimeline() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = this.forms.editTimeline._id || 'none';

            let formattedYearCount = (this.forms.editTimeline.yearCount || '').trim();
            if (/^\d+$/.test(formattedYearCount)) {
                formattedYearCount = 'Year ' + formattedYearCount;
            }

            await axios.put('/career-graph/event/' + itemId, {
                employeeId,
                index: this.forms.editTimeline.index,
                title: this.forms.editTimeline.title,
                financialYear: this.forms.editTimeline.financialYear,
                yearCount: formattedYearCount
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editTimeline = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async deleteTimeline(item, index) {
        if (!confirm('Are you sure you want to delete this timeline event?')) return;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const itemId = item._id || 'none';
            await axios.delete('/career-graph/event/' + employeeId + '/' + itemId + '?index=' + index, {
                headers: { 'x-auth-token': token }
            });
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        }
    },

    openManageGraphData(type) {
        this.manageType = type;
        this.dialogs.manageGraphData = true;
    },
    editGraphDataRow(item, index) {
        this.forms.editGraphDataRow = {
            _id: item._id || null,
            index: index,
            type: this.manageType,
            financialYear: item.financialYear || '',
            yearCount: item.yearCount || '',
            ctc: item.amount || null,
            grade: item.grade || '',
            pmr: item.rating || null
        };
        this.dialogs.editGraphDataRow = true;
    },
    async updateGraphDataRow() {
        this.saving = true;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const type = this.forms.editGraphDataRow.type;
            const itemId = this.forms.editGraphDataRow._id || 'none';

            let formattedYearCount = (this.forms.editGraphDataRow.yearCount || '').trim();
            if (/^\d+$/.test(formattedYearCount)) {
                formattedYearCount = 'Year ' + formattedYearCount;
            }

            await axios.put('/career-graph/graph-data/' + type + '/' + itemId, {
                employeeId,
                index: this.forms.editGraphDataRow.index,
                financialYear: this.forms.editGraphDataRow.financialYear,
                yearCount: formattedYearCount,
                ctc: this.forms.editGraphDataRow.ctc,
                grade: this.forms.editGraphDataRow.grade,
                pmr: this.forms.editGraphDataRow.pmr
            }, { headers: { 'x-auth-token': token } });
            this.dialogs.editGraphDataRow = false;
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        } finally {
            this.saving = false;
        }
    },
    async deleteGraphDataRow(item, index) {
        if (!confirm('Are you sure you want to delete this graph entry?')) return;
        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            const type = this.manageType;
            const itemId = item._id || 'none';
            await axios.delete('/career-graph/graph-data/' + type + '/' + employeeId + '/' + itemId + '?index=' + index, {
                headers: { 'x-auth-token': token }
            });
            await this.fetchGraphData();
        } catch (e) {
            console.error(e);
        }
    },
    onDragStart(event, section, index) {
        this.draggedSection = section;
        this.draggedIndex = index;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', index);
        }
    },
    onDragOver(event) {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
    },
    async onDrop(event, section, targetIndex) {
        event.preventDefault();
        if (this.draggedSection !== section || this.draggedIndex === null || this.draggedIndex === targetIndex) {
            this.onDragEnd();
            return;
        }
        const arr = [...(this.graphData[section] || [])];
        const itemToMove = arr.splice(this.draggedIndex, 1)[0];
        arr.splice(targetIndex, 0, itemToMove);

        this.$set(this.graphData, section, arr);
        this.onDragEnd();

        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            await axios.put(`/career-graph/reorder/${section}/${employeeId}`, {
                items: arr
            }, { headers: { 'x-auth-token': token } });
        } catch (e) {
            console.error("Error reordering items:", e);
            await this.fetchGraphData();
        }
    },
    onDragEnd() {
        this.draggedSection = null;
        this.draggedIndex = null;
    },
    async moveItem(section, index, direction) {
        const arr = [...(this.graphData[section] || [])];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= arr.length) return;

        const temp = arr[index];
        arr[index] = arr[newIndex];
        arr[newIndex] = temp;

        this.$set(this.graphData, section, arr);

        try {
            const token = localStorage.getItem('token');
            const employeeId = this.graphData.employeeId._id;
            await axios.put(`/career-graph/reorder/${section}/${employeeId}`, {
                items: arr
            }, { headers: { 'x-auth-token': token } });
        } catch (e) {
            console.error("Error reordering items:", e);
            await this.fetchGraphData();
        }
    },
    formatDate(dateStr) {
        if(!dateStr) return '';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatFY(fyStr) {
        if(!fyStr) return '';
        const parts = fyStr.split('-');
        if(parts.length === 2) {
            return parts[0].slice(-2) + '-' + parts[1].slice(-2);
        }
        return fyStr;
    },
    calculateTenure(dojStr) {
        if(!dojStr) return 'N/A';
        const doj = new Date(dojStr);
        const now = new Date();
        const diffYears = (now - doj) / (1000 * 60 * 60 * 24 * 365.25);
        return diffYears.toFixed(1) + ' Years';
    },
    formatExperienceBeforeWTI(expBefore) {
        if (!expBefore) return '0 Years';
        let str = String(expBefore).trim();
        if (str.toLowerCase().includes('year') || str.toLowerCase().includes('yr')) return str;
        return str + ' Years';
    },
    calculateTotalExperience(dojStr, expBefore) {
        let tenureYears = 0;
        if(dojStr) {
            const doj = new Date(dojStr);
            const now = new Date();
            tenureYears = (now - doj) / (1000 * 60 * 60 * 24 * 365.25);
        }
        
        let beforeYears = 0;
        if(expBefore) {
            let match = String(expBefore).match(/[\d.]+/);
            if(match && !isNaN(parseFloat(match[0]))) {
                beforeYears = parseFloat(match[0]);
            }
        }
        
        if (tenureYears === 0 && beforeYears === 0) return 'N/A';
        return (tenureYears + beforeYears).toFixed(1) + ' Years';
    },
    getFYForCategory(cat) {
        let entry = (this.graphData.ctcTrend || []).find(d => d.yearCount === cat) ||
                    (this.graphData.gradeProgression || []).find(d => d.yearCount === cat) ||
                    (this.graphData.pmrTrend || []).find(d => d.yearCount === cat);
        return entry ? entry.financialYear : '';
    },
    getValForCategory(type, cat) {
        let entry;
        if(type === 'ctc') entry = (this.graphData.ctcTrend || []).find(d => d.yearCount === cat);
        if(type === 'grade') entry = (this.graphData.gradeProgression || []).find(d => d.yearCount === cat);
        if(type === 'pmr') entry = (this.graphData.pmrTrend || []).find(d => d.yearCount === cat);
        
        if(!entry) return null;
        if(type === 'ctc') return entry.amount;
        if(type === 'grade') return entry.grade;
        if(type === 'pmr') return entry.rating;
    },
    async downloadPDF() {
        this.isPrinting = true;
        await this.$nextTick();
        
        // Allow ApexCharts time to re-render SVG with data labels
        setTimeout(async () => {
            const element = document.querySelector('.cg-container');
            if (element) {
                try {
                    const scrollHeight = element.scrollHeight;
                    const scrollWidth = element.scrollWidth;

                    const canvas = await html2canvas(element, { 
                        scale: 2, 
                        useCORS: true,
                        logging: false,
                        backgroundColor: '#f5f5f5',
                        height: scrollHeight,
                        windowHeight: scrollHeight,
                        width: scrollWidth,
                        windowWidth: scrollWidth,
                        ignoreElements: (node) => {
                            return node.classList && (node.classList.contains('print-hide') || node.classList.contains('v-btn'));
                        }
                    });
                    
                    const imgData = canvas.toDataURL('image/png');
                    
                    // Initialize A4 Landscape PDF
                    const pdf = new jsPDF('l', 'mm', 'a4');
                    const pageWidth = pdf.internal.pageSize.getWidth();   // 297 mm
                    const pageHeight = pdf.internal.pageSize.getHeight(); // 210 mm
                    
                    const margin = 4;
                    const renderW = pageWidth - (margin * 2);  // 289 mm full horizontal width
                    let renderH = (canvas.height * renderW) / canvas.width;
                    const maxH = pageHeight - (margin * 2);    // 202 mm
                    
                    if (renderH > maxH) {
                        renderH = maxH;
                    }
                    
                    const y = Math.max(margin, (pageHeight - renderH) / 2);
                    
                    pdf.addImage(imgData, 'PNG', margin, y, renderW, renderH);
                    pdf.save(`${(this.empData.name || 'Career').replace(/\s+/g, '_')}_Career_Graph.pdf`);
                } catch(err) {
                    console.error("Error generating PDF", err);
                    alert("Error downloading PDF: " + (err.message || err));
                } finally {
                    this.isPrinting = false;
                }
            } else {
                this.isPrinting = false;
            }
        }, 500);
    }
  }
};
</script>

<style scoped>
/* Main Layout to prevent scroll */
.cg-container {
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
  overflow: hidden; 
}

/* PDF Print / Export Layout */
.cg-container.is-printing {
  width: 1280px !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  background-color: #f5f5f5 !important;
}
.cg-container.is-printing .cg-header {
  height: 48px !important;
  padding-left: 20px !important;
  padding-right: 20px !important;
}
.cg-container.is-printing .cg-header img {
  max-height: 32px !important;
}
.cg-container.is-printing .cg-content {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  padding: 8px 16px !important;
}
.cg-container.is-printing .row-1 {
  margin-bottom: 8px !important;
  flex: none !important;
  height: auto !important;
}
.cg-container.is-printing .row-1 .cg-card {
  min-height: 150px !important;
  max-height: none !important;
  height: auto !important;
  overflow: visible !important;
}
.cg-container.is-printing .row-2 {
  flex: none !important;
  height: 145px !important;
  margin-bottom: 8px !important;
  overflow: visible !important;
}
.cg-container.is-printing .row-2 .cg-card {
  overflow: visible !important;
}
.cg-container.is-printing .row-3 {
  flex: none !important;
  height: auto !important;
  margin-bottom: 4px !important;
  overflow: visible !important;
}
.cg-container.is-printing .row-3 .cg-card {
  overflow: visible !important;
}
.cg-container.is-printing .chart-container {
  height: 220px !important;
  min-height: 220px !important;
  overflow: visible !important;
}
.cg-container.is-printing .overflow-y-auto {
  overflow: visible !important;
  max-height: none !important;
  height: auto !important;
}
.cg-container.is-printing .custom-small-toggles {
  display: none !important;
}
.cg-header {
  height: 60px;
  background-color: #004d40;
  flex-shrink: 0;
}
.cg-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

/* Row Heights - using flexbox to fill available space optimally */
.row-1 { flex: 0 0 auto; height: auto; min-height: 0; }
.row-2 { flex: 0 0 auto; height: 150px; min-height: 0; }
.row-3 { flex: 1 1 0; min-height: 250px; }

/* Cards & Fields - Hide Scrollbars completely */
.cg-card {
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important;
  border-color: #e0e0e0 !important;
  overflow-x: hidden !important;
  -ms-overflow-style: none !important;
  scrollbar-width: none !important;
}

.cg-card ::-webkit-scrollbar,
.no-scrollbar::-webkit-scrollbar,
.overflow-y-auto::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

.no-scrollbar {
  -ms-overflow-style: none !important;
  scrollbar-width: none !important;
}

/* Timeline specific styling */
.timeline-wrapper {
  margin-top: 0px;
}
.timeline-line {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 3px;
  background-color: #005f32;
  transform: translateY(-50%);
  z-index: 0;
}
.timeline-node {
  position: absolute; 
  width: 140px;
  margin-left: -70px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}
.timeline-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #005f32;
  border: 2px solid #fff;
  z-index: 2;
  position: relative;
}
.bg-red {
  background-color: #d32f2f !important;
}
.timeline-title-top {
  position: absolute;
  bottom: 14px;
  width: 200px;
  left: 50%;
  transform: translateX(-50%);
}
.timeline-title-bottom {
  position: absolute;
  top: 14px;
  width: 200px;
  left: 50%;
  transform: translateX(-50%);
}

/* Toggles */
.custom-small-toggles {
  transform: scale(0.85);
  transform-origin: right center;
}
.custom-small-toggles .v-input--selection-controls__input::before {
  display: none !important;
}

/* Chart container must not overflow */
.chart-container {
  min-height: 0;
  position: relative;
}
.chart-container > div {
  height: 100% !important;
}

.w-100 { width: 100%; }
.position-relative { position: relative; }

/* Responsive adjustments for mobile and tablets */
@media (max-width: 960px) {
  .cg-container {
    height: auto !important;
    min-height: calc(100vh - 64px);
    overflow: visible !important;
  }
  .cg-content {
    overflow: visible !important;
    display: block !important;
  }
  .row-1, .row-3 {
    flex: none !important;
    height: auto !important;
  }
  .row-1 .cg-card {
    min-height: 200px !important;
    height: auto !important;
  }
  .row-2 {
    flex: none !important;
    height: 150px !important; 
  }
  .chart-container {
    height: 300px !important;
  }
  .timeline-wrapper {
    overflow-x: auto !important;
    overflow-y: hidden !important;
    -webkit-overflow-scrolling: touch;
  }
  .timeline-wrapper > div {
    min-width: 800px !important;
  }
}

.position-absolute { position: absolute; }
.list-style-none { list-style: none; }
.cursor-pointer { cursor: pointer; }

/* Drag and Drop styling */
.cursor-grab {
  cursor: grab !important;
  cursor: -webkit-grab !important;
}
.cursor-grab:active {
  cursor: grabbing !important;
  cursor: -webkit-grabbing !important;
}
.drag-icon-svg {
  color: #555;
  transition: color 0.15s ease, transform 0.15s ease;
  user-select: none;
}
.cursor-grab:hover .drag-icon-svg {
  color: #005f32;
}
.dragging-item {
  opacity: 0.45 !important;
  border: 1px dashed #005f32 !important;
  background-color: #f0f7f4 !important;
  border-radius: 4px;
}

/* Print adjustments */
@media print {
  body * { visibility: hidden; }
  .cg-container, .cg-container * { visibility: visible; }
  .cg-container { 
    position: absolute !important; 
    left: 0 !important; 
    top: 0 !important; 
    width: 100vw !important;
    height: auto !important; 
    overflow: visible !important; 
  }
  .cg-header, .cg-header * { display: none !important; visibility: hidden !important; }
  .print-hide, .print-hide * { display: none !important; visibility: hidden !important; }
  
  .row-1, .row-2, .row-3 { height: auto !important; page-break-inside: avoid; }
  .cg-card { margin-bottom: 20px; box-shadow: none !important; border: 1px solid #000 !important; }
  .chart-container { height: 300px !important; }
  /* hide toggles in print */
  .d-flex.justify-end.align-center.mb-2 { display: none !important; }
}
</style>
